import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import {
  SystemNotificationContext,
  type NewSystemNotification,
  type SystemNotification,
} from './SystemNotificationContext';

interface SystemNotificationProviderProps {
  children: ReactNode;
  initialNotifications?: SystemNotification[];
}

function createNotificationId() {
  return globalThis.crypto?.randomUUID?.() ?? `system-notification-${Date.now()}-${Math.random()}`;
}

export function SystemNotificationProvider({ children, initialNotifications = [] }: SystemNotificationProviderProps) {
  const [notifications, setNotifications] = useState<SystemNotification[]>(initialNotifications);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => new Set());

  const addNotification = useCallback((notification: NewSystemNotification) => {
    const id = notification.id ?? createNotificationId();
    const nextNotification = { ...notification, id };

    setNotifications((current) => [...current.filter((item) => item.id !== id), nextNotification]);
    setDismissedIds((current) => {
      if (!current.has(id)) return current;
      const next = new Set(current);
      next.delete(id);
      return next;
    });

    return id;
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications((current) => current.filter((notification) => notification.id !== id));
    setDismissedIds((current) => {
      if (!current.has(id)) return current;
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }, []);

  const dismissNotification = useCallback((id: string) => {
    setDismissedIds((current) => new Set(current).add(id));
  }, []);

  const visibleNotifications = useMemo(
    () => notifications.filter((notification) => !dismissedIds.has(notification.id)),
    [dismissedIds, notifications],
  );

  const value = useMemo(
    () => ({
      notifications: visibleNotifications,
      addNotification,
      removeNotification,
      dismissNotification,
    }),
    [addNotification, dismissNotification, removeNotification, visibleNotifications],
  );

  return <SystemNotificationContext.Provider value={value}>{children}</SystemNotificationContext.Provider>;
}
